import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/bootstrap/Button';
import Avatar from '../../../components/Avatar';
import { useAppDispatch, useAppSelector } from '../../../custom/redux/reduxHooks';
import Dropdown, { DropdownMenu, DropdownToggle } from '../../../components/bootstrap/Dropdown';
import { logoutUser } from '../../../custom/redux/reducers/authReducer';
import Icon from '../../../components/icon/Icon';
import classNames from 'classnames';
import { formatMoney } from '../../../helpers/helpers';

const CommonHeaderProfile = () => {
	const { user } = useAppSelector((state) => state.auth);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const getInitials = (firstname: string, lastname: string) => {
		return `${firstname?.charAt(0) || ''}${lastname?.charAt(0) || ''}`.toUpperCase();
	};

	const [showBalance, setShowBalance] = useState(false);

	return user ? (
		<div className='col d-flex align-items-center'>
			<Dropdown>
				<DropdownToggle hasIcon={false}>
					<div className='d-flex align-items-center user-profile-toggle p-0'>
						<div
							className='me-3 text-end d-none d-lg-block'
							style={{ maxWidth: '180px' }}>
							<div className='fw-bold fs-6 mb-0 text-dark uppercase text-truncate'>
								{`${user?.firstname} ${user?.lastname}`}
							</div>
							<div className='text-muted'>
								<small
									className='capitalize text-truncate d-block'
									title={user?.role?.roleName}>
									{user?.role?.roleName}
								</small>
							</div>
						</div>
						<div className='user-avatar-wrapper cursor-pointer'>
							<div
								className='user-avatar d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10'
								style={{ width: '45px', height: '45px' }}>
								<span className='text-primary fw-bold'>
									{getInitials(user.firstname, user.lastname)}
								</span>
							</div>
						</div>
					</div>
				</DropdownToggle>
				<DropdownMenu isAlignmentEnd className='py-0 dropdown-profile'>
					<div className='profile-header px-4 py-4 bg-primary bg-opacity-10 border-bottom'>
						<div className='d-flex align-items-center w-100'>
							<div
								className='me-3 d-flex align-items-center justify-content-center rounded-circle bg-white flex-shrink-0'
								style={{ width: '70px', height: '70px' }}>
								<span className='text-primary fw-bold fs-3'>
									{getInitials(user.firstname, user.lastname)}
								</span>
							</div>
							<div className='flex-grow-1 min-w-0'>
								<h5 className='mb-1 fw-bold text-truncate name-text'>{`${user.firstname} ${user.lastname}`}</h5>
								<div className='d-flex align-items-center role-container'>
									<Icon
										icon='Shield'
										className='me-2 text-primary flex-shrink-0'
									/>
									<span
										className='fs-6 text-primary fw-medium role-text'
										title={user?.role?.roleName}>
										{user?.role?.roleName || 'NA'}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div className='profile-info px-4 py-4'>
						<div className='location-section d-flex align-items-center mb-4'>
							<div className='location-icon-wrapper me-3 bg-primary'>
								<Icon
									icon='LocationOn'
									className='text-white'
									style={{ fontSize: '1.5rem' }}
								/>
							</div>
							<div>
								<div className='text-muted small'>Current Location</div>
								<div className='fw-bold'>
									{user?.userLocation?.locationName || 'NA'}
								</div>
							</div>
						</div>

						<div className='balance-item mb-4'>
							<div className='d-flex align-items-center'>
								<div className='balance-icon-wrapper me-3 bg-primary'>
									<Icon
										icon='Store'
										className='text-white'
										style={{ fontSize: '1.5rem' }}
									/>
								</div>
								<div>
									<div className='d-flex align-items-center gap-2'>
										<div className='text-muted small'>Location Balance</div>
									</div>

									<div className='flex items-center gap-2'>
										<div className='fw-bold text-success'>
											{showBalance
												? formatMoney(
														user?.userLocation?.locationBalance
															?.balance || 0,
														`${user?.userLocation?.currency || 'GMD'}`,
												  )
												: '••••••••'}
										</div>
										<Icon
											icon={showBalance ? 'VisibilityOff' : 'Visibility'}
											style={{ fontSize: '1.3rem' }}
											className='text-muted cursor-pointer'
											onClick={() => setShowBalance(!showBalance)}
										/>
									</div>
								</div>
							</div>
						</div>

						<div className='balance-item'>
							<div className='d-flex align-items-center'>
								<div className='balance-icon-wrapper me-3 bg-primary'>
									<Icon
										icon='AccountBalanceWallet'
										className='text-white'
										style={{ fontSize: '1.5rem' }}
									/>
								</div>
								<div>
									<div className='d-flex align-items-center justify-content-between'>
										<div className='text-muted small'>Your Balance</div>
									</div>
									<div className='fw-bold text-primary'>
										{formatMoney(user?.userBalance?.balance || 0, 'GMD')}
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className='px-4 py-3 border-top'>
						<div className='d-grid gap-2'>
							<Button
								size='lg'
								color='primary'
								isLight
								className='d-flex align-items-center justify-content-center py-2'
								onClick={() => navigate('/profile')}>
								<Icon icon='Person' className='me-2' />
								<span className='fs-6'>Profile Settings</span>
							</Button>

							<div className='my-2 d-flex align-items-center justify-content-center'>
								<div className='separator-line flex-grow-1'></div>
								<div className='mx-3'>
									<div className='separator-dot'></div>
								</div>
								<div className='separator-line flex-grow-1'></div>
							</div>

							<Button
								size='lg'
								color='danger'
								isLight
								className='d-flex align-items-center justify-content-center py-2'
								onClick={() => dispatch(logoutUser())}>
								<Icon icon='Logout' className='me-2' />
								<span className='fs-6'>Sign Out</span>
							</Button>
						</div>
					</div>
				</DropdownMenu>
			</Dropdown>

			<style>
				{`
					.user-profile-toggle {
						transition: all 0.2s ease;
					}
					.user-profile-toggle:hover {
						opacity: 0.9;
					}
					.user-avatar {
						border: 2px solid var(--bs-primary);
						transition: transform 0.2s ease;
					}
					.user-avatar:hover {
						transform: scale(1.05);
					}
					.dropdown-profile {
						width: 350px !important;
						min-width: 350px !important;
						margin-top: 0.75rem;
						box-shadow: 0 0.75rem 1.5rem rgba(0, 0, 0, 0.12);
						border: none;
						border-radius: 1rem;
						overflow: hidden;
					}
					.profile-header {
						position: relative;
					}
					.profile-header::after {
						content: '';
						position: absolute;
						bottom: -5px;
						left: 0;
						right: 0;
						height: 5px;
						background: linear-gradient(to right, var(--bs-primary), var(--bs-info));
						opacity: 0.1;
					}
					.info-icon {
						width: 40px;
						height: 40px;
						display: flex;
						align-items: center;
						justify-content: center;
						border-radius: 12px;
						background: var(--bs-primary);
						background-opacity: 0.1;
					}
					.location-info:hover, .balance-info:hover, .location-balance-info:hover {
						transform: translateX(5px);
						transition: transform 0.2s ease;
					}
					.profile-info h6 {
						font-size: 0.9rem;
						letter-spacing: 0.3px;
					}
						.location-icon-wrapper, .balance-icon-wrapper {
							display: flex;
							align-items: center;
							justify-content: center;
							width: 35px;
							height: 35px;
							border-radius: 0.5rem;
							background: var(--bs-light);
						}
						.balance-section {
							display: flex;
							align-items: center;
						}
						.balance-item {
							// min-width: 200px;
						}
						.balance-text {
							font-family: monospace;
							letter-spacing: 0.1em;
						}
						.separator-line {
							height: 1px;
							background: linear-gradient(to right, transparent, var(--bs-border-color), transparent);
							opacity: 0.6;
						}
						.separator-dot {
							width: 6px;
							height: 6px;
							border-radius: 50%;
							background: var(--bs-border-color);
							opacity: 0.8;
							animation: pulse 2s infinite;
						}
						@keyframes pulse {
							0%, 100% { opacity: 0.8; transform: scale(1); }
							50% { opacity: 1; transform: scale(1.2); }
						}
						.role-text {
							line-height: 1.3;
							white-space: nowrap;
							overflow: hidden;
							text-overflow: ellipsis;
							max-width: calc(100% - 32px);
						}
						.role-container {
							max-width: 100%;
							overflow: hidden;
						}
						.name-text {
							max-width: 100%;
						}
						.min-w-0 {
							min-width: 0;
						}
						.flex-shrink-0 {
							flex-shrink: 0;
						}
				`}
			</style>
		</div>
	) : null;
};

export default CommonHeaderProfile;
